import { useContext, useEffect, useState } from "react";
import useCustomNavigate from "../../hooks/useCustomNavigate";
import useCurrentGame from "../../hooks/useCurrentGame";
import useBrowserStorage from "../../hooks/useBrowserStorage";
import PersonalAccount from "../../store/PersonalAccount";
import General from "../../store/General";
import Game from "../../store/Game";
import { useLocation } from "react-router-dom";
import useAuth from "../../hooks/useAuth";
import INTOUCH_API from "../../services/Api";
import { SOCKET_EVENTS, SocketContext } from "../../components/SocketProvider";
import useCurrentUserData from "../../hooks/useCurrentUserData";
import ROUTES from "../../utils/routes.js";
import { MODAL_TYPES } from "../../utils/emitter";
import useModalActions from "../../hooks/useModalActions";
import { useTranslation } from "react-i18next";
import { isPWA, isWebView } from "../../utils/helpers";
import useFirebaseWebNotification from "../../hooks/useFirebaseWebNotification";
import config from "../../config";
import { CURRENT_GAME_KEY } from "../../utils/constants";
import * as Sentry from "@sentry/react";
export default function useController() {
  const { sendEvent } = useContext(SocketContext);
  const [isEditingActivated, setIsEditingActivated] = useState(false);
  const { token } = useAuth();
  const { navigateTo } = useCustomNavigate();
  const { setGame } = useCurrentGame();
  const { getUserData } = useCurrentUserData();
  const { getItem, removeItem } = useBrowserStorage();
  const location = useLocation();
  const { openModal } = useModalActions();
  const { t } = useTranslation();
  const { connectFirebaseWebNotification } = useFirebaseWebNotification();

  useEffect(() => {
    if (
      location &&
      location.state &&
      location.state.event === "GENDER_NOTIFICATION"
    ) {
      openModal(MODAL_TYPES.GENDER_NOTIFICATION, {
        link: location.state.link,
        type: "auth",
        message: t("genderModal.authText"),
      });
    }
  }, [location.state]);

  const activateEditProfile = () => {
    navigateTo("edit");
    setIsEditingActivated(true);
  };
  const deactivateEditProfile = () => {
    navigateTo(ROUTES.PERSONAL_ACCOUNT_PAGE);
    setIsEditingActivated(false);
  };

  const startGame = async (game) => {
    // Обновление 16.07: продажа повторных тестов отменена — PENDING_PAYMENT-гейта
    // больше нет (бэк такие игры не создаёт, существующие разблокированы)
    const IS_USER_NOT_JOINED = !game.invitedUserId;
    setGame(game);
    Game.setGame(game);
    if (IS_USER_NOT_JOINED && game.answers.length === Game.cards.length) {
      openModal(MODAL_TYPES.LINK_WAS_NOT_SENT);
      return;
    }

    const matches = Game.countPartnerGameMatches(game.id);
    const user = getUserData();

    PersonalAccount.setStatusGame({
      isFinishedCompromise: game?.isFinishedCompromise,
      isFinishedMarathon: game?.isFinishedMarathon,
    });
    PersonalAccount.manageLoading(game, "start");
    await General.checkAnswers(game.id);
    PersonalAccount.manageLoading(game, "stop");

    if (matches) {
      if (!PersonalAccount.hasViewedRules(game.id)) {
        navigateTo(ROUTES.ACCEPT_RULES_PAGE);
        return;
      }
      navigateTo(ROUTES.MATCHES_PAGE);
    } else {
      sendEvent(SOCKET_EVENTS.JOIN_GAME, {
        room: game.id,
        userId: user.id,
      });
      if (game.type === "DEMO" && !PersonalAccount.hasViewedMeet(game.id)) {
        navigateTo(ROUTES.MEET_PAGE);
        return;
      }
      if (
        !PersonalAccount.hasViewedGreetings(game.id) &&
        game.type !== "DEMO"
      ) {
        navigateTo(ROUTES.GREETINGS_PAGE);
        return;
      }
      navigateTo(ROUTES.IN_GAME_PAGE);
    }
  };

  useEffect(() => {
    const ref = getItem("ref", "session");
    if (ref && ref.length && token) {
      const addBusinessPartnerForUser = async () => {
        await INTOUCH_API.requestManager(
          "addBusinessPartnerForUser",
          { ref },
          (response) => {
            removeItem("ref", "session");
          },
          (error) => {
            console.log(error);
            Sentry.captureException(error, {
              tags: {
                feature: "addBusinessPartnerForUser",
                component: "PersonalAccountPage",
              },
              level: "warning",
            });
          }
        );
      };

      addBusinessPartnerForUser();
    }
  }, []);

  useEffect(() => {
    const isPermissionViewed = localStorage.getItem("isPermissionViewed");
    Promise.all([
      PersonalAccount.getPersonalAccountData(),
      Game.getCards("questions_cards", null, null),
      Game.getAllGamesMatches(),
    ]);
    if (
      !isWebView() &&
      isPWA() &&
      typeof Notification !== "undefined" &&
      Notification.permission !== "granted" &&
      !isPermissionViewed
    ) {
      openModal(MODAL_TYPES.PERMISSION);
      localStorage.setItem("isPermissionViewed", 1);
    } else {
      connectFirebaseWebNotification();
    }
  }, []);

  useEffect(() => {
    if (location.pathname === ROUTES.PERSONAL_ACCOUNT_PAGE) {
      setIsEditingActivated(false);
    }
    if (location.pathname === ROUTES.PERSONAL_ACCOUNT_EDIT_PAGE) {
      setIsEditingActivated(true);
    }
  }, [location.pathname]);

  // Авто-переход покупателя в свайпер +57 после оплаты. PayPlus возвращает на
  // /personal-account (свой redirect_url) раньше, чем срабатывает поллинг на
  // /payment, поэтому доводим здесь: если стоит флажок оплаченного набора и игра
  // уже выдана (hasExtendedSet) и набор не дозаполнен — уводим к 57 картам.
  // Флажок одноразовый (чистим сразу), при неуспешной оплате hasExtendedSet=false
  // и перехода не будет; в остальных случаях остаётся вход «Open +57 cards» в ЛК.
  useEffect(() => {
    const pendingId = localStorage.getItem("pendingExt57GameId");
    if (!pendingId) return;
    (async () => {
      await PersonalAccount.getPersonalAccountData();
      localStorage.removeItem("pendingExt57GameId");
      const g = PersonalAccount.games?.find(
        (x) => String(x.id) === String(pendingId)
      );
      if (
        g &&
        g.hasExtendedSet &&
        (g.answers?.length ?? 0) < +config.totalCards + 57
      ) {
        Game.setGame(g);
        localStorage.setItem(CURRENT_GAME_KEY, JSON.stringify(g));
        // item 6: вход в +57 через страницу инструкции
        navigateTo(ROUTES.EXTENDED_57_INSTRUCTION_PAGE, {
          state: { gameId: g.id },
        });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return {
    user: PersonalAccount.user,
    startGame,
    isEditingActivated,
    activateEditProfile,
    deactivateEditProfile,
  };
}
